import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { HALO_SOFTWARE_BENCHMARK } from "@/config/haloMarketing";

const proofPoints = [
  {
    title: "LightningFlow Enterprise platform",
    value: "One connected platform",
    meaning: "Bring secure connectivity, network visibility and everyday operations into one clear view.",
    note: "Available for enterprise demonstrations and deployment discussions.",
  },
  {
    title: "Halo Software",
    value: "Coming soon",
    meaning: "Halo is designed to make private connectivity simple, with a clear view of your protection.",
    note: "See the Halo product page for platform availability and download announcements.",
  },
  {
    title: "FPGA Acceleration",
    value: "Supported workloads",
    meaning: "LightningFlow can accelerate supported networking workloads while retaining health visibility, software fallback and controlled rollback.",
    note: "Performance varies by workload; benchmark details are published for validated configurations.",
  },
];

const consumerStatus = [
  {
    title: "SecureShield",
    value: "App Store edition",
    meaning: "Fourteen real macOS security checks with guided fixes, each re-checked. Pricing and store availability follow the live product page gates.",
    to: "/secureshield",
  },
  {
    title: "SecureShield Pro",
    value: "Available now",
    meaning: "SecureShield plus Network Guard: the same fourteen checks, one-click fixes and a quiet watch over every connection your Mac makes.",
    to: "/secureshield-pro",
  },
  {
    title: "SecureAI",
    value: "Coming soon",
    meaning: "Private connections for compatible AI tools. Public Mac downloads and supported integrations are being prepared.",
    to: "/secureai",
  },
];

export default function Evidence() {
  const improvement = Math.round(HALO_SOFTWARE_BENCHMARK.measured_improvement_pct);
  return <>
    <SEO
      title="Trust Centre — LightningFlow"
      description="Explore LightningFlow platform assurance, security alignment, Halo measured claims and consumer product status."
      canonicalPath="/evidence"
    />
    <section className="border-b border-ink-100 bg-ink-50/40">
      <div className="lf-container py-20 md:py-28">
        <div className="lf-eyebrow">Trust Centre</div>
        <h1 className="mt-5 font-display text-5xl font-black tracking-[-.035em] md:text-6xl">Confidence built on transparency.</h1>
        <p className="mt-7 max-w-3xl text-lg leading-relaxed text-ink-600">Explore LightningFlow’s product assurance, security alignment and validated platform capabilities in one place.</p>
      </div>
    </section>

    <section className="lf-section">
      <div className="lf-container grid gap-4 lg:grid-cols-3">
        {proofPoints.map((point) => (
          <article className="border border-ink-100 bg-white p-7" key={point.title}>
            <div className="font-mono text-[10px] uppercase tracking-[.18em] text-ink-500">{point.title}</div>
            <h2 className="mt-4 font-display text-3xl font-bold">{point.value}</h2>
            <div className="mt-5 font-display text-sm font-bold text-ink-900">What this means</div>
            <p className="mt-2 leading-relaxed text-ink-600">{point.meaning}</p>
            <p className="mt-5 border-l-2 border-electric pl-4 text-sm leading-relaxed text-ink-500">{point.note}</p>
          </article>
        ))}
      </div>
    </section>

    <section id="halo-speed" className="scroll-mt-28 border-y border-ink-100 bg-[#031124] text-white">
      <div className="lf-container grid gap-10 py-16 md:grid-cols-[1.1fr_.9fr] md:items-center md:py-20">
        <div>
          <div className="lf-eyebrow text-cyan-200">Measured claim</div>
          <h2 className="mt-4 font-display text-4xl font-black md:text-5xl">Halo “up to {improvement}% faster”</h2>
          <p className="mt-5 max-w-xl text-lg leading-relaxed text-slate-300">
            {HALO_SOFTWARE_BENCHMARK.test_note} Baseline {HALO_SOFTWARE_BENCHMARK.baseline.throughput_mbps.toFixed(0)} Mbps versus {HALO_SOFTWARE_BENCHMARK.optimised.throughput_mbps.toFixed(0)} Mbps with Halo under the same software-only conditions ({HALO_SOFTWARE_BENCHMARK.successful_runs} successful runs).
          </p>
        </div>
        <div className="border border-white/15 bg-white/5 p-7">
          <div className="font-mono text-[10px] uppercase tracking-[.18em] text-cyan-200">{HALO_SOFTWARE_BENCHMARK.method}</div>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div>
              <div className="text-sm text-slate-400">{HALO_SOFTWARE_BENCHMARK.baseline.label}</div>
              <div className="mt-2 font-display text-3xl font-black">{HALO_SOFTWARE_BENCHMARK.baseline.throughput_mbps.toFixed(0)} Mbps</div>
            </div>
            <div>
              <div className="text-sm text-slate-400">{HALO_SOFTWARE_BENCHMARK.optimised.label}</div>
              <div className="mt-2 font-display text-3xl font-black text-cyan-200">{HALO_SOFTWARE_BENCHMARK.optimised.throughput_mbps.toFixed(0)} Mbps</div>
            </div>
          </div>
          <Link className="lf-btn mt-8 border border-white/40 text-white hover:border-white" to="/halo">Review Halo SuperVPN</Link>
        </div>
      </div>
    </section>

    <section className="lf-section bg-ink-50/40">
      <div className="lf-container">
        <div className="lf-eyebrow">Consumer portfolio</div>
        <h2 className="mt-4 font-display text-4xl font-black">Honest status for Halo-family products.</h2>
        <div className="mt-10 grid gap-4 lg:grid-cols-3">
          {consumerStatus.map((item) => (
            <Link key={item.title} to={item.to} className="border border-ink-100 bg-white p-7 transition-colors hover:border-electric">
              <div className="font-mono text-[10px] uppercase tracking-[.18em] text-ink-500">{item.title}</div>
              <h3 className="mt-4 font-display text-2xl font-bold">{item.value}</h3>
              <p className="mt-3 text-sm leading-relaxed text-ink-600">{item.meaning}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  </>;
}
